import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { produccionApi, errorMessage } from '../utils/produccionApi';
import { STAGE_LABELS, formatKg, formatDateTime } from '../utils/produccion';
import useWideLayout from '../utils/useWideLayout';
import '../styles/produccion.css';

const TABS = [
  { key: 'activas', label: 'Activas' },
  { key: 'terminadas', label: 'Terminadas' },
  { key: 'finalizadas', label: 'Finalizadas' },
  { key: 'anuladas', label: 'Anuladas' },
];
const SEARCH_DELAY_MS = 250;

// Where the row button goes depends on how far the order got. Only an admin
// or Robin's production_closer closes a finished order, and the closer does
// nothing else.
function rowAction(order, userRole) {
  const base = `/production/${order.po_number}`;
  if (userRole === 'production_closer') {
    return order.stage === 'terminada'
      ? { label: 'Cerrar', to: `${base}/cerrar`, primary: true }
      : { label: 'Ver', to: base, primary: false };
  }
  switch (order.stage) {
    case 'por_retirar': return { label: 'Retirar', to: `${base}/retiro`, primary: true };
    case 'retirando': return { label: 'Seguir retiro', to: `${base}/retiro`, primary: true };
    case 'lista_para_terminar': return { label: 'Terminar', to: `${base}/terminar`, primary: true };
    case 'terminada':
      if (userRole === 'admin') return { label: 'Cerrar', to: `${base}/cerrar`, primary: true };
      return { label: 'Ver', to: base, primary: false };
    default: return { label: 'Ver', to: base, primary: false };
  }
}

function ProduccionHome() {
  const { token, userRole, logout } = useAuth();
  const navigate = useNavigate();
  const wide = useWideLayout();
  const [tab, setTab] = useState('activas');
  const [orders, setOrders] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [waiting, setWaiting] = useState(0);
  const [query, setQuery] = useState('');
  const [search, setSearch] = useState('');
  const latest = useRef(0);

  const load = useCallback(async (tabKey, pageNumber, q = '') => {
    const request = latest.current + 1;
    latest.current = request;
    setLoading(true);
    setError('');
    const find = q ? `&q=${encodeURIComponent(q)}` : '';
    const res = await produccionApi.get(`orders/?tab=${tabKey}&page=${pageNumber}${find}`, token);
    if (request !== latest.current) return;
    setLoading(false);
    if (!res.ok) {
      setError(errorMessage(res));
      return;
    }
    const results = (res.data && res.data.results) || [];
    setOrders((current) => (pageNumber === 1 ? results : [...current, ...results]));
    setHasMore(Boolean(res.data && res.data.has_more));
    setWaiting((res.data && res.data.counts && res.data.counts.terminadas) || 0);
    setPage(pageNumber);
  }, [token]);

  // Finalizadas searches a short pause after the last key.
  useEffect(() => {
    const next = query.trim();
    if (next === search) return undefined;
    const timer = setTimeout(() => setSearch(next), SEARCH_DELAY_MS);
    return () => clearTimeout(timer);
  }, [query, search]);

  const q = tab === 'finalizadas' ? search : '';
  useEffect(() => {
    setOrders([]);
    setHasMore(false);
    load(tab, 1, q);
  }, [tab, q, load]);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const go = (to) => navigate(to);

  const issuesBadge = (order) => (order.issues > 0
    ? <span className="prod-chip red">{`⚠ ${order.issues}`}</span> : null);

  // Legacy orders never went through the retiro: their lines count nothing.
  const isLegacy = (order) => order.legacy || order.stage === 'antigua';
  const progress = (order) => (isLegacy(order) ? '' : `${order.lines_done} de ${order.lines_total}`);
  const productName = (order) => {
    const products = order.products && order.products.length ? order.products : [order.product].filter(Boolean);
    if (!products.length) return 'Orden antigua';
    return products.length > 1 ? `${products[0].name} +${products.length - 1}` : products[0].name;
  };
  const holder = (order) => (order.retiro_holder && ['por_retirar', 'retirando'].includes(order.stage)
    ? <div className="prod-sub" style={{ margin: 0 }}>{`Retirando: ${order.retiro_holder}`}</div> : null);

  const actionButton = (order) => {
    const action = rowAction(order, userRole);
    return (
      <button
        type="button"
        className={`prod-btn ${action.primary ? 'blue' : 'ghost'}`}
        style={{ margin: 0, padding: '8px 14px', width: 'auto', display: 'inline-block', fontSize: 14 }}
        onClick={(event) => { event.stopPropagation(); go(action.to); }}
      >
        {action.label}
      </button>
    );
  };

  return (
    <div className="prod-page">
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between' }}>
        <h1 className="prod-title">Producción</h1>
        <div style={{ display: 'flex', gap: 8 }}>
          {userRole === 'admin' && (
            <button type="button" className="prod-btn ghost" style={{ width: 'auto', margin: 0, padding: '8px 12px', fontSize: 14 }} onClick={() => navigate('/init')}>← Inicio</button>
          )}
          <button type="button" className="prod-btn ghost" style={{ width: 'auto', margin: 0, padding: '8px 12px', fontSize: 14 }} onClick={handleLogout}>Cerrar sesión</button>
        </div>
      </div>

      {userRole !== 'production_closer' && (
        <button type="button" className="prod-btn green" onClick={() => navigate('/production/nueva')}>+ Nueva producción</button>
      )}

      <div className="prod-tabs">
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`prod-tab${tab === t.key ? ' on' : ''}`}
            onClick={() => setTab(t.key)}
          >
            {t.key === 'terminadas' && waiting > 0 ? `${t.label} (${waiting})` : t.label}
          </button>
        ))}
      </div>

      {tab === 'finalizadas' && (
        <input
          className="prod-input"
          aria-label="Buscar en finalizadas"
          placeholder="N°, código o nombre"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      )}

      {error && <div className="prod-bad">{error}</div>}
      {!error && !loading && orders.length === 0 && (
        <div className="prod-sub">No hay órdenes en esta pestaña.</div>
      )}

      {wide && orders.length > 0 && (
        <table className="prod-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ textAlign: 'left' }}>
              <th>N°</th><th>Producto</th><th>Estado</th><th>Materia prima</th><th>Creada</th><th />
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => (
              <tr
                key={order.po_number}
                style={{ cursor: 'pointer', borderTop: '1px solid #e3e7eb' }}
                onClick={() => go(`/production/${order.po_number}`)}
              >
                <td><b>{order.po_number}</b></td>
                <td>{productName(order)}</td>
                <td>
                  {STAGE_LABELS[order.stage] || order.stage} {issuesBadge(order)}
                  {progress(order) && <div className="prod-sub" style={{ margin: 0 }}>{progress(order)}</div>}
                  {holder(order)}
                </td>
                <td>{`${formatKg(order.planned_kg)} kg`}</td>
                <td>{formatDateTime(order.created_at)}</td>
                <td>{actionButton(order)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {!wide && orders.map((order) => (
        <div
          key={order.po_number}
          className={`prod-card${order.stage === 'lista_para_terminar' ? ' green' : ''}${order.stage === 'anulada' ? ' red' : ''}`}
          role="presentation"
          onClick={() => go(`/production/${order.po_number}`)}
        >
          <span className="prod-chip">{STAGE_LABELS[order.stage] || order.stage}</span>
          <div className="n">{order.po_number} {issuesBadge(order)}</div>
          <div className="d">{productName(order)}</div>
          {holder(order)}
          <div className="d">
            {[progress(order), `${formatKg(order.planned_kg)} kg`, formatDateTime(order.created_at)].filter(Boolean).join(' · ')}
          </div>
          <div style={{ marginTop: 6 }}>{actionButton(order)}</div>
        </div>
      ))}

      {hasMore && (
        <button type="button" className="prod-btn ghost" disabled={loading} onClick={() => load(tab, page + 1, q)}>Ver más</button>
      )}
    </div>
  );
}

export default ProduccionHome;
